import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", message: "Verifying your payment…" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "No session_id in URL." });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const max = 12;

    async function poll() {
      attempts += 1;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setState({ status: "success", message: "Welcome to Pro!" });
          toast.success("Pro unlocked — Alpha Signals are now available.");
          setTimeout(() => navigate("/signals"), 1500);
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "error", message: "Checkout session expired. Please try again." });
          return;
        }
        if (attempts >= max) {
          setState({ status: "error", message: "Payment status check timed out. Refresh the page or contact support." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", message: e.response?.data?.detail || "Failed to verify payment." });
      }
    }
    poll();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="billing-success-page">
      <div className="border border-border bg-[#0F0F0F] p-8 max-w-md w-full">
        {state.status === "polling" && (
          <>
            <Loader2 size={36} className="text-[#007AFF] animate-spin mx-auto" />
            <div className="font-[Chivo] text-xl mt-4 text-center">Confirming Payment</div>
            <p className="text-sm text-neutral-400 mt-2 text-center">{state.message}</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 size={36} className="text-[#00E676] mx-auto" />
            <div className="font-[Chivo] text-2xl mt-4 text-center">{state.message}</div>
            <p className="text-sm text-neutral-400 mt-2 text-center">Redirecting to Alpha Signals…</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <AlertCircle size={36} className="text-[#FF3B30] mx-auto" />
            <div className="font-[Chivo] text-xl mt-4 text-center">Couldn't verify payment</div>
            <p className="text-sm text-neutral-400 mt-2 text-center">{state.message}</p>
            <Button
              data-testid="back-to-signals"
              className="w-full mt-6 bg-[#007AFF] hover:bg-[#3395FF]"
              onClick={() => navigate("/signals")}
            >
              Back to Signals
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
